import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useGetReviewClassifications } from "../api/queries/review.queries";
import { getReviewDetail } from "../api/requests/review.requests";
import { QUERY_KEYS } from "../api/config";
import { ClassificationReviewItem } from "../types/review";
import ReviewGrid, { ReviewGridViewState } from "../components/review/review-grid";
import ReviewFilterBar from "../components/review/review-filter-bar";
import ReviewDetailPanel from "../components/review/review-detail-panel";
import ReviewCarousel from "../components/review/review-carousel";
import EventPanel from "../components/review/event-panel";
import BulkActionsBar from "../components/review/bulk-actions-bar";
import RecentReviews from "../components/review/recent-reviews";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries";
import { useGetAllLocations } from "@/locations/api/queries/location.queries";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { ML_EXPORTS_URL } from "../config/ml-exports-urls";
import { useBulkSelectionStore } from "../stores/useBulkSelectionStore";
import { useMarqueeSelect } from "../hooks/useMarqueeSelect";
import { useReviewFilters } from "../hooks/useReviewFilters";
import { useReviewPanels } from "../hooks/useReviewPanels";
import { useReviewUrlSync } from "../hooks/useReviewUrlSync";
import ResumeSelectionBanner from "../components/review/resume-selection-banner";
import {
  useReviewLayoutStore,
  MIN_CARDS_PER_ROW,
  MAX_CARDS_PER_ROW,
  DEFAULT_EMPHASIS_LABEL,
} from "../stores/useReviewLayoutStore";
import {
  sortReviewItems,
  REVIEW_ORDERING_OPTIONS,
  DEFAULT_REVIEW_ORDERING,
} from "../utils/sort-review-items";
import type { ReviewOrdering } from "../types/review";
import {
  buildEventRuns,
  nextFrameIndex,
  nextEventFirstFrame,
} from "../utils/grid-navigation";
import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { Slider } from "@/ui/components/ui/slider";
import { Checkbox } from "@/ui/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { LayoutGrid, ArrowDownUp, Sparkles, Rows3 } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { useGetClassificationTaxonomy } from "../api/queries/taxonomy.queries";

/**
 * Classification Review page.
 *
 * Filter/scope state and the stacked-overlay state are owned by dedicated
 * hooks so this page stays focused on data fetching, view-state derivation
 * and layout:
 *   - useReviewFilters — scope + applied/revealed filters (the request model)
 *   - useReviewPanels  — detail / event / walkthrough overlay state
 */
const ReviewPage = () => {
  const { t } = useTranslation("mlExports");
  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);
  const { data: locationsData } = useGetAllLocations();
  const locations = locationsData?.data ?? [];

  useSetBreadcrumbs([
    {
      path: ML_EXPORTS_URL.ROOT,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading,
    },
    {
      path: ML_EXPORTS_URL.REVIEW,
      breadcrumb: t("breadcrumbs.review"),
    },
  ]);

  // ─── Filter & scope state (single source of truth for the query) ──
  const {
    selectedLocationId,
    selectedMachineIds,
    appliedFilters,
    hasScopeSelected,
    hasRefinement,
    activeFilters,
    handleApplyFilters,
    handleClearAllFilters,
    handleClearFilter,
    handleApplyScope,
    presets,
    history,
    savePreset,
    deletePreset,
    applySnapshot,
  } = useReviewFilters();

  // ─── Overlay / panel state ────────────────────────────────────────
  const {
    detailImageId,
    walkthroughIds,
    openEventUuid,
    openDetail,
    closeDetail,
    openWalkthrough,
    closeWalkthrough,
    openEvent,
    closeEvent,
    openInDetailFromEvent,
  } = useReviewPanels();

  // ─── Bulk selection store ─────────────────────────────────────────
  const selectedIds = useBulkSelectionStore((s) => s.ids);
  const toggleSelection = useBulkSelectionStore((s) => s.toggle);
  const captureFilterSnapshot = useBulkSelectionStore((s) => s.captureFilterSnapshot);
  const addMany = useBulkSelectionStore((s) => s.addMany);

  const captureScope = useBulkSelectionStore((s) => s.captureScope);

  const gridRef = useRef<HTMLDivElement>(null);

  // Mirror the session into the address bar so it can be pasted to a
  // teammate. Must run before the query so an incoming link's scope is in
  // place for the first fetch.
  useReviewUrlSync();

  // Identity of the view the current selection belongs to, so a restored
  // selection can be checked against where the reviewer actually is.
  const currentScope = useMemo(
    () => ({
      locationId: selectedLocationId,
      machineIds: [...selectedMachineIds],
      fromDate: activeFilters.start_time,
      toDate: activeFilters.end_time,
    }),
    [selectedLocationId, selectedMachineIds, activeFilters.start_time, activeFilters.end_time],
  );

  useEffect(() => {
    captureScope(currentScope);
  }, [currentScope, captureScope]);

  // Capture filter snapshot for the bulk-selection "loaded vs hidden" split.
  // `activeFilters` is memoized so its identity only changes when one of its
  // fields actually changes; `captureFilterSnapshot` is a stable Zustand
  // action. Depending on both directly is correct and avoids the drift-prone
  // hand-maintained field list this effect used to carry.
  useEffect(() => {
    captureFilterSnapshot(activeFilters);
  }, [activeFilters, captureFilterSnapshot]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetReviewClassifications(activeFilters, {
    // Hook already guards on scope, but be explicit:
    enabled: hasScopeSelected,
    // Keep previous pages visible while a new query runs, so the grid
    // doesn't blank out when a refinement chip commits. Instead we show
    // a dim overlay via viewState.isRefetching.
    keepPreviousData: true,
    staleTime: 30_000,
  });

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.metadata?.totalItems ?? items.length;

  // ─── Sort ordering — a BACKEND param ─────────────────────────────
  // `ordering` lives in appliedFilters (the request model), so changing it
  // re-queries with the sort applied across the full filtered result set
  // before pagination. `sortReviewItems` below is only a light in-page pass
  // to keep an event's frames visually contiguous.
  const ordering = appliedFilters.ordering ?? DEFAULT_REVIEW_ORDERING;
  const setOrdering = (value: ReviewOrdering) =>
    handleApplyFilters({ ordering: value });
  const cardsPerRow = useReviewLayoutStore((s) => s.cardsPerRow);
  const setCardsPerRow = useReviewLayoutStore((s) => s.setCardsPerRow);
  const groupHighlight = useReviewLayoutStore((s) => s.groupHighlight);
  const setGroupHighlight = useReviewLayoutStore((s) => s.setGroupHighlight);
  const emphasisLabel = useReviewLayoutStore((s) => s.emphasisLabel);
  const setEmphasisLabel = useReviewLayoutStore((s) => s.setEmphasisLabel);
  const emphasisEnabled = useReviewLayoutStore((s) => s.emphasisEnabled);
  const setEmphasisEnabled = useReviewLayoutStore((s) => s.setEmphasisEnabled);
  const layoutMode = useReviewLayoutStore((s) => s.layoutMode);
  const setLayoutMode = useReviewLayoutStore((s) => s.setLayoutMode);
  const { data: emphasisTaxonomy } = useGetClassificationTaxonomy();

  const emphasisOptions = useMemo(() => {
    const set = new Set<string>([DEFAULT_EMPHASIS_LABEL, emphasisLabel]);
    emphasisTaxonomy?.families.forEach((f) =>
      f.members.forEach((m) => set.add(m.canonical)),
    );
    return [...set];
  }, [emphasisTaxonomy, emphasisLabel]);

  const sortedItems = useMemo(
    () => sortReviewItems(items, ordering),
    [items, ordering]
  );

  // ─── View state — drives every empty/loading branch in ReviewGrid ─
  const viewState: ReviewGridViewState = !hasScopeSelected
    ? "onboarding"
    : isError
      ? "error"
      : isLoading
        ? "loading"
        : items.length === 0
          ? hasRefinement
            ? "empty_filtered"
            : "empty_scope"
          : "ready";

  // Refetch while we already have something on screen — dim overlay, not skeleton.
  const isRefetching = isFetching && !isLoading && viewState === "ready";

  // ─── Marquee (drag-to-select) ─────────────────────────────────────
  const handleMarqueeSelect = useCallback(
    (ids: number[]) => addMany(ids),
    [addMany],
  );

  const { selectionRect: marqueeRect, isSelecting: isMarqueeSelecting, isMarqueeMode, onOverlayMouseDown } = useMarqueeSelect({
    containerRef: gridRef,
    cardSelector: "[data-image-id]",
    onSelect: handleMarqueeSelect,
    enabled: viewState === "ready",
  });

  // ─── Handlers ─────────────────────────────────────────────────────
  const handleToggleSelect = useCallback(
    (imageId: number) => toggleSelection(imageId),
    [toggleSelection]
  );

  const handleCardClick = useCallback(
    (item: ClassificationReviewItem) => {
      if (isMarqueeMode) return;
      openDetail(item.image_id);
    },
    [isMarqueeMode, openDetail]
  );

  // ─── Keyboard review — focus cursor + event-aware arrow navigation ──
  // ←/→ step frames; ↑/↓ jump events (first frame); Enter/E opens detail;
  // Esc clears focus. Handler is inactive while a panel is open or while
  // typing in a filter input, so it never fights those.
  const [focusedImageId, setFocusedImageId] = useState<number | null>(null);

  const eventRuns = useMemo(() => buildEventRuns(sortedItems), [sortedItems]);

  // Drop focus if the focused card is no longer in the loaded/filtered set.
  useEffect(() => {
    if (
      focusedImageId != null &&
      !sortedItems.some((i) => i.image_id === focusedImageId)
    ) {
      setFocusedImageId(null);
    }
  }, [sortedItems, focusedImageId]);

  useEffect(() => {
    if (viewState !== "ready") return;
    const handler = (e: KeyboardEvent) => {
      // The event panel owns all keys (it has its own frame/event/cycle nav).
      if (openEventUuid != null) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;

      // When the detail panel is open, we still drive arrow navigation (and
      // move the open detail to the new frame), but leave Enter/Esc/labeling
      // keys to the detail panel's own handler (save / close / cycle).
      const detailOpen = detailImageId != null;

      const count = sortedItems.length;
      if (count === 0) return;
      // Anchor on the open detail frame when it's open, else the focus cursor —
      // so arrows move relative to what the user is actually looking at.
      const anchorId = detailOpen ? detailImageId : focusedImageId;
      const curIdx =
        anchorId == null
          ? -1
          : sortedItems.findIndex((i) => i.image_id === anchorId);

      let nextIdx: number | null = null;
      switch (e.key) {
        case "ArrowRight":
          nextIdx = nextFrameIndex(curIdx, 1, count);
          break;
        case "ArrowLeft":
          nextIdx = nextFrameIndex(curIdx, -1, count);
          break;
        case "ArrowDown":
          nextIdx = nextEventFirstFrame(curIdx, 1, eventRuns);
          break;
        case "ArrowUp":
          nextIdx = nextEventFirstFrame(curIdx, -1, eventRuns);
          break;
        case "Enter":
        case "e":
        case "E":
          // Detail open → let the panel handle Enter (save); don't reopen.
          if (detailOpen) return;
          if (curIdx >= 0) {
            e.preventDefault();
            openDetail(sortedItems[curIdx].image_id);
          }
          return;
        case "Escape":
          // Detail open → let the panel close itself.
          if (detailOpen) return;
          if (focusedImageId != null) {
            e.preventDefault();
            setFocusedImageId(null);
          }
          return;
        default:
          return;
      }
      e.preventDefault();
      if (nextIdx >= 0) {
        const nextId = sortedItems[nextIdx].image_id;
        setFocusedImageId(nextId);
        // Keep the open detail in sync with the newly focused frame.
        if (detailOpen) openDetail(nextId);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    viewState,
    sortedItems,
    eventRuns,
    focusedImageId,
    detailImageId,
    openEventUuid,
    openDetail,
  ]);

  // Prefetch + warm the adjacent frames so arrowing through the (remounting)
  // detail panel lands on cached data and an already-decoded image — instant,
  // no skeleton flash.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (detailImageId == null) return;
    const idx = sortedItems.findIndex((i) => i.image_id === detailImageId);
    if (idx < 0) return;
    const neighborIds = [
      sortedItems[idx - 1]?.image_id,
      sortedItems[idx + 1]?.image_id,
    ].filter((id): id is number => id != null);

    let cancelled = false;
    neighborIds.forEach(async (id) => {
      const data = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.REVIEW_DETAIL, id],
        queryFn: () => getReviewDetail(id),
      });
      if (!cancelled && data?.signed_url) {
        const img = new Image();
        img.src = data.signed_url;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [detailImageId, sortedItems, queryClient]);

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  // ─── Header subtitle — honest per view state ──────────────────────
  const subtitle = (() => {
    switch (viewState) {
      case "onboarding":
        return t("review.subtitle.onboarding");
      case "loading":
        return t("review.subtitle.loading");
      case "empty_scope":
        return t("review.subtitle.emptyScope");
      case "empty_filtered":
        return t("review.subtitle.emptyFiltered", {
          total: formatNumber(totalCount),
        });
      case "error":
        return t("review.subtitle.error");
      case "ready":
        return t(
          isRefetching
            ? "review.subtitle.readyApprox"
            : "review.subtitle.ready",
          {
            total: formatNumber(totalCount),
            loaded: formatNumber(items.length),
          },
        );
    }
  })();

  return (
    <div className="flex h-full min-h-0">
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">{t("review.title")}</h1>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
            {/* openDetail is a stable callback, so RecentReviews' memo holds */}
            <RecentReviews onImageClick={openDetail} />
          </div>

          <ReviewFilterBar
            appliedFilters={appliedFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilter={handleClearFilter}
            onClearAll={handleClearAllFilters}
            hasRefinement={hasRefinement}
            locations={locations}
            selectedLocationId={selectedLocationId}
            selectedMachineIds={selectedMachineIds}
            onApplyScope={handleApplyScope}
            hasScopeSelected={hasScopeSelected}
            presets={presets}
            history={history}
            onSavePreset={savePreset}
            onDeletePreset={deletePreset}
            onApplySnapshot={applySnapshot}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4">
          {/* Sort + density toolbar. Density is client-side; Sort is not —
              it commits `ordering` to the request model so the backend sorts
              the full result set. Only meaningful once the grid is populated. */}
          {viewState === "ready" && (
            <div className="flex items-center justify-between gap-2 pb-3">
              <div className="flex items-center gap-3">
                {/* Sort — segmented, separate from Filters. Every supported
                    backend ordering is a primary segment, so the active sort
                    is always readable without opening anything. Nothing here
                    touches a filter, and no filter touches `ordering`. */}
                <div
                  className="flex items-center gap-1.5"
                  role="radiogroup"
                  aria-label={t("review.toolbar.sortOrder")}
                >
                  <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
                  <div className="flex items-center gap-0.5 rounded-full bg-muted/60 p-0.5">
                    {REVIEW_ORDERING_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={ordering === opt.value}
                        onClick={() => setOrdering(opt.value)}
                        className={cn(
                          "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200",
                          ordering === opt.value
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toggle the dashed "same event" group borders. Applies to
                    both layouts: in event-rows it borders multi-frame event
                    runs the same way the grid does. */}
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                  <Checkbox
                    checked={groupHighlight}
                    onCheckedChange={(c) => setGroupHighlight(c === true)}
                  />
                  {t("review.toolbar.groupByEvent")}
                </label>

                {/* Emphasise a label across the grid (e.g. boulder). */}
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select value={emphasisLabel} onValueChange={setEmphasisLabel}>
                    <SelectTrigger className="h-7 w-[130px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emphasisOptions.map((c) => (
                        <SelectItem key={c} value={c} className="text-xs">
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Checkbox
                    checked={emphasisEnabled}
                    onCheckedChange={(v) => setEmphasisEnabled(v === true)}
                    aria-label={t("review.toolbar.enableEmphasis")}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Layout mode: grid vs one-event-per-row */}
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={layoutMode === "grid" ? "secondary" : "ghost"}
                    className="h-7 px-2"
                    onClick={() => setLayoutMode("grid")}
                    aria-label={t("review.toolbar.gridLayout")}
                    title={t("review.toolbar.gridLayout")}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant={layoutMode === "event-rows" ? "secondary" : "ghost"}
                    className="h-7 px-2"
                    onClick={() => setLayoutMode("event-rows")}
                    aria-label={t("review.toolbar.eventRowsLayout")}
                    title={t("review.toolbar.eventRowsLayout")}
                  >
                    <Rows3 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {layoutMode === "grid" && (
                  <>
                    <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {t("review.toolbar.perRow", {
                        value: formatNumber(cardsPerRow),
                      })}
                    </span>
                    <Slider
                      aria-label={t("review.toolbar.cardsPerRow")}
                      value={[cardsPerRow]}
                      onValueChange={(v) => setCardsPerRow(v[0])}
                      min={MIN_CARDS_PER_ROW}
                      max={MAX_CARDS_PER_ROW}
                      step={1}
                      className="w-32"
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {hasScopeSelected && (
            <ResumeSelectionBanner
              currentScope={currentScope}
              locationName={
                locations.find((l) => l.id === selectedLocationId)?.name
              }
            />
          )}

          <ReviewGrid
            viewState={viewState}
            items={sortedItems}
            isRefetching={isRefetching}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage ?? false}
            error={isError ? (error as Error | null) : null}
            scopeImageCount={totalCount}
            alertStatusFilter={appliedFilters.alert_status_in}
            onLoadMore={handleLoadMore}
            onRetry={() => refetch()}
            onClearFilters={handleClearAllFilters}
            selectedItems={selectedIds}
            onToggleSelect={handleToggleSelect}
            onCardClick={handleCardClick}
            focusedImageId={focusedImageId}
            cardsPerRow={cardsPerRow}
            showGroupHighlight={groupHighlight}
            layoutMode={layoutMode}
            gridRef={gridRef}
            marqueeRect={marqueeRect}
            isMarqueeSelecting={isMarqueeSelecting}
            isMarqueeMode={isMarqueeMode}
            onOverlayMouseDown={onOverlayMouseDown}
          />
        </div>

        {/* Sticky bulk actions bar — appears when selection > 0 */}
        <BulkActionsBar
          loadedItems={items}
          onWalkThrough={openWalkthrough}
          scopeLocationId={selectedLocationId}
          scopeMachineIds={selectedMachineIds}
        />
      </div>

      {detailImageId && (
        <ReviewDetailPanel
          // Remount per frame so each frame gets fresh state (signed image,
          // staged corrections). Adjacent frames are prefetched + warmed (see
          // the prefetch effect) so the remount lands instantly — no flash.
          key={detailImageId}
          imageId={detailImageId}
          onClose={closeDetail}
          onOpenEvent={openEvent}
        />
      )}

      {openEventUuid && (
        <EventPanel
          eventUuid={openEventUuid}
          activeImageId={detailImageId}
          onClose={closeEvent}
          onOpenInDetail={openInDetailFromEvent}
        />
      )}

      {walkthroughIds && walkthroughIds.length > 0 && (
        <ReviewCarousel
          imageIds={walkthroughIds}
          onClose={closeWalkthrough}
        />
      )}
    </div>
  );
};

export default ReviewPage;
